import { IEvent } from "../interfaces/models/IEvent";
import { getDayKeyInTimezone } from "./dates";
import {
  formatFlexibleEventDateLabel,
  getEventDateDisplayMode,
} from "./eventFlexibleDate";

export type EventTimingStatus = "upcoming" | "live" | "past";
export type EventCountdownTone = "default" | "live";

interface EventCountdownBadge {
  label: string;
  tone: EventCountdownTone;
}

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

const getCurrentTimestamp = (): number => Math.floor(Date.now() / 1000);

export const isLiveEvent = (
  event: IEvent,
  nowTimestamp: number = getCurrentTimestamp(),
): boolean =>
  typeof event.startDate === "number" &&
  typeof event.endDate === "number" &&
  event.startDate <= nowTimestamp &&
  event.endDate >= nowTimestamp;

export const getEventTimingStatus = (
  event: IEvent,
  nowTimestamp: number = getCurrentTimestamp(),
): EventTimingStatus => {
  if (isLiveEvent(event, nowTimestamp)) {
    return "live";
  }

  if (typeof event.endDate === "number" && event.endDate < nowTimestamp) {
    return "past";
  }

  if (typeof event.startDate !== "number") {
    return "upcoming";
  }

  const eventDayKey = getDayKeyInTimezone(event.startDate, event.timeZone);
  const todayDayKey = getDayKeyInTimezone(nowTimestamp, event.timeZone);

  return eventDayKey >= todayDayKey ? "upcoming" : "past";
};

export const isCurrentEvent = (
  event: IEvent,
  nowTimestamp: number = getCurrentTimestamp(),
): boolean => getEventTimingStatus(event, nowTimestamp) !== "past";

export const getEventCountdownBadge = (
  event: IEvent,
  nowTimestamp: number = getCurrentTimestamp(),
): EventCountdownBadge => {
  if (event.comingSoonDate || typeof event.startDate !== "number") {
    const mode = getEventDateDisplayMode(event);
    return {
      label:
        mode === "monthYear"
          ? formatFlexibleEventDateLabel(event.comingSoonDateMessage, "Date TBD")
          : "Date TBD",
      tone: "default",
    };
  }

  if (isLiveEvent(event, nowTimestamp)) {
    return {
      label: "Live",
      tone: "live",
    };
  }

  const eventDayKey = getDayKeyInTimezone(event.startDate, event.timeZone);
  const todayDayKey = getDayKeyInTimezone(nowTimestamp, event.timeZone);
  const eventDate = new Date(`${eventDayKey}T00:00:00Z`);
  const todayDate = new Date(`${todayDayKey}T00:00:00Z`);
  const diffInDays = Math.round(
    (eventDate.getTime() - todayDate.getTime()) / ONE_DAY_IN_MS,
  );

  if (diffInDays <= 0) {
    return {
      label: "Today",
      tone: "default",
    };
  }

  return {
    label: `${diffInDays} day${diffInDays === 1 ? "" : "s"} left`,
    tone: "default",
  };
};
